import { ICompileService } from "angular";
import {
  FieldConfigurationHelperService,
  vsName as FieldConfigurationHelperServiceName,
} from "../../../records/forms/formly/fieldConfigurationHelpers";
import {
  FormSubmitArgs,
  FormViewConfig,
  FormViewService,
  FormViewServiceProvider,
  vsProviderName as FormViewServiceProviderName,
  vsServiceName as FormViewServiceName,
} from "../../../records/forms/formView";
import { RecordDataSource } from "../../../records/grids";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../records/grids/columns/columnDefinitionService";
import { FilterProvider } from "../../../records/grids/columns/filters/FilterProvider";
import { LookupFilter } from "../../../records/grids/columns/filters/subFilters/LookupFilter";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../records/server/serverUtility";
import {
  GridConfiguration,
  RecordGridViewServiceProvider,
  vsProviderName as RecordGridViewServiceProviderName,
} from "../../../records/views/recordGridView";
import fieldHelpers from "../../../ui-formly/fieldHelpers";
import {
  LookupManagementServiceProvider,
  vsProviderName as LookupManagementServiceProviderName,
} from "../components/lookupManagement";
import { MachineZone } from "../models/machineConfiguration";

MachineZoneConfig.$inject = [
  FormViewServiceProviderName,
  RecordGridViewServiceProviderName,
  LookupManagementServiceProviderName,
];

export const vsObjectName = "machineZone";

export function MachineZoneConfig(
  formViewServiceProvider: FormViewServiceProvider,
  recordGridViewServiceProvider: RecordGridViewServiceProvider,
  lookupManagementServiceProvider: LookupManagementServiceProvider
) {
  formViewServiceProvider.addFormViewConfig(vsObjectName, getFormConfig);
  recordGridViewServiceProvider.addGridConfiguration(
    vsObjectName,
    getGridConfig
  );
  lookupManagementServiceProvider.addLookup(vsObjectName, {
    displayName: "Zone",
    config: [
      ServerUtilityName,
      (serverUtility: ServerUtility) => {
        return {
          fetchFormModel: (id) =>
            serverUtility.querySingle({
              url: "/api/machines/MachineZone",
              query: { Id: id },
            }),
        };
      },
    ],
  });
}

getGridConfig.$inject = [
  "$compile",
  ServerUtilityName,
  ColumnDefinitionServiceName,
];
function getGridConfig(
  $compile: ICompileService,
  serverUtility: ServerUtility,
  columnDefinitionService: ColumnDefinitionService
): GridConfiguration {
  return {
    columns: [
      columnDefinitionService.getStringColDef({
        field: "PropertyName",
        header: "Property",
        filter: new FilterProvider("PropertyName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["PropertyId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/properties",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "MachineAreaName",
        header: "Area",
        filter: new FilterProvider("MachineAreaName", $compile)
          .defaultStringFilters()
          .addFilter(
            "=",
            new LookupFilter({
              displayName: "=",
              queryFieldNames: ["MachineAreaId"],
              serverQuery: (search) =>
                serverUtility.lookupQuery({
                  url: "/api/machines/MachineArea",
                  search,
                }),
            })
          )
          .getGridFilterFactory(),
      }),
      columnDefinitionService.getStringColDef({
        field: "Name",
        header: "Zone",
        filter: new FilterProvider("Name", $compile)
          .defaultStringFilters()
          .getGridFilterFactory(),
      }),
    ],
    gridDataSource: new RecordDataSource(
      serverUtility,
      "/api/machines/MachineZone"
    ),
    defaultSort: "PropertyName,MachineAreaName,Name",
    namePlural: "Zones",
    nameSingular: "Zone",
    showAddButton: true,
    showAddButtonPermission: "machineEdit",
    showSelectButtons: false,
  };
}

getFormConfig.$inject = [
  FieldConfigurationHelperServiceName,
  FormViewServiceName,
];
function getFormConfig(
  fieldConfigurationHelperService: FieldConfigurationHelperService,
  formViewService: FormViewService
): FormViewConfig {
  return {
    fields: [
      fieldConfigurationHelperService.selectAsyncField({
        key: "PropertyId",
        name: "PropertyId",
        label: "Property",
        required: true,
        getDefaultItem: (model) => {
          return { Id: model.PropertyId, Name: model.PropertyName };
        },
        queryUrl: "/api/properties",
      }),
      fieldConfigurationHelperService.selectAsyncField({
        key: "MachineAreaId",
        name: "MachineAreaId",
        label: "Area",
        required: true,
        disableDefaultQueryCaching: true,
        getDefaultItem: (model) => {
          return { Id: model.MachineAreaId, Name: model.MachineAreaName };
        },
        expressionProperties: {
          "templateOptions.readonly": (_, __, scope) =>
            scope.model?.PropertyId === undefined,
        },
        watcher: [
          {
            expression: "model.PropertyId",
            listener: (_, newValue, oldValue, scope) => {
              if (scope.model && newValue !== oldValue) {
                scope.model.MachineAreaId = undefined;
              }
            },
          },
        ],
        queryBuilder: (search, model) => {
          return {
            PropertyId: model.PropertyId,
            NameContains: search,
          };
        },
        queryUrl: "/api/machines/MachineArea",
      }),
      fieldHelpers.simpleStringField({
        key: "Name",
        label: "Zone",
        required: true,
      }),
    ],
    title: (model) => (model.Id ? "Edit Zone" : "Create Zone"),
    name: vsObjectName,
    submitForm: (args: FormSubmitArgs) => {
      const model = <MachineZone>args.model;
      return formViewService.saveForm({
        formObject: model,
        formViewController: args.formViewController,
        url: "/api/machines/MachineZone",
      });
    },
  };
}
